import { useContext, useMemo } from 'react'
import styled from 'styled-components'

import DraggableItemWrapper from 'tripPlanner/components/DragAndDrop/DraggableItemWrapper'
import ItineraryDroppable from 'tripPlanner/components/DragAndDrop/ItineraryDroppable'
import TimelineTripItem from 'tripPlanner/components/Timeline/TimelineTripItem'
import { ViewModeContext } from 'tripPlanner/contexts/ViewModeContext'
import type { TripItem } from 'tripPlanner/types/tripItem'
import { NO_DATES, canSetItemDatesFromOnlyStartDate } from 'tripPlanner/utils/itinerary'
import { rem } from 'polished'
import { useAppSelector } from 'hooks/reduxHooks'
import { getTripFilters } from 'tripPlanner/utils/selectors'
import { filterTripItems, getNonEmptyProductFilters } from 'tripPlanner/utils/summary'
import TripPlannerFilters from '../../../components/TripItemFilters/TripPlannerFilters'
import { isLeBooking } from 'tripPlanner/utils'
import { SectionContainer } from './common'

const FilterContainer = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  padding-top: ${rem(8)};
  padding-bottom: ${rem(8)};
`

interface Props {
  items: Array<TripItem>
  draggingItemId?: string
}

function SortableNonDateItems({
  items,
  draggingItemId,
}: Props) {
  const { viewMode } = useContext(ViewModeContext)

  const availableSubFilters = useMemo(() => {
    return getNonEmptyProductFilters(items)
  }, [items])
  const selectedFilters = useAppSelector(getTripFilters)
  const filteredItems = useMemo(
    () => filterTripItems(items, selectedFilters),
    [items, selectedFilters],
  )

  const draggingItemIndex = filteredItems.findIndex(
    (item) => item.id === draggingItemId,
  )
  const isDraggingItemInList = draggingItemIndex !== -1

  return (
    <SectionContainer className="saved-section" direction="vertical" data-itinerarydate={NO_DATES}>
      {availableSubFilters.length > 1 && (
        <FilterContainer>
          <TripPlannerFilters
            availableSubFilters={availableSubFilters}
            selectedFilters={selectedFilters}
            itemCount={items.length}
            style="scroll"
          />
        </FilterContainer>
      )}
      {filteredItems.map((item, i) => (
        <div key={item.id}>
          <ItineraryDroppable
            id={`${NO_DATES}:before:${item.id}`}
            disabled={isDraggingItemInList && i === draggingItemIndex + 1}
          />
          <DraggableItemWrapper
            id={item.id}
            data={{
              lockedToDate: (!canSetItemDatesFromOnlyStartDate(item) || isLeBooking(item)) ? NO_DATES : undefined,
            }}
            handlesOnlyOnHover={viewMode === 'detail'}
          >
            <TimelineTripItem item={item} presentationType="start" variant="drawer" />
          </DraggableItemWrapper>
        </div>
      ))}
      <ItineraryDroppable
        id={`${NO_DATES}:end`}
        disabled={
          isDraggingItemInList && draggingItemIndex === filteredItems.length - 1
        }
      />
    </SectionContainer>
  )
}

export default SortableNonDateItems
